/**
 * =============================================================================
 * collection.js
 * =============================================================================
 * Collection page functionality
 * =============================================================================
 */

 var Collection = {
  container: undefined, // The HTML element containing the products
  sectionId: undefined, // ID of the results page section, so we know what section to request via fetch
  grid: undefined, // The grid element containing the product tiles
  endlessMode: undefined, // Data injected by collection-grid.liquid
  latestPage: undefined, // The latest page we've asynchronously loaded
  earliestPage: undefined, // The earliest page we've asynchronously loaded
  lastProductViewed: undefined, // The product ID of the last product viewed from localStorage
  pagesLoaded: [], // An array of page indices we've already loaded
  observer: undefined, // IntersectionObserver for pagination tiles

  /**
   * Initialisation
   */
  init: function() {
    this.container = document.getElementById("products-container");
    if(!this.container){
      return;
    }
    this.grid = document.getElementById("products-grid");
    this.sectionId = this.container.dataset.sectionId;
    this.latestPage = parseInt(this.container.dataset.currentPage);
    this.earliestPage = this.latestPage;
    this.pagesLoaded.push(this.latestPage);

    this.lastProductViewed = localStorage.getItem("product:viewed");
    localStorage.removeItem("product:viewed");

    // Autoscroll to tile of last viewed product if it exists. This relies on
    // us being on the right PAGE as well (otherwise the product won't be there
    // to scroll to)
    if(this.lastProductViewed) {
      let lastViewedProductTile = this.container.querySelector(`[data-product-id="${this.lastProductViewed}"]`);
      if(lastViewedProductTile){
        lastViewedProductTile.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }

    this.setupEventListeners();
  },

  /**
   * Setup event listeners
   */
  setupEventListeners: function() {
    // Listen for 'load more' clicks, trigger the load. We need to listen on the
    // container, rather than directly on the button, as the button is
    // re-rendered / dynamically injected each time a filter is applied.
    this.container.addEventListener('click', function(event) {
      if(event.target.closest("a")?.classList.contains("load-more")){
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onLoadMoreClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
      };
      if(event.target.closest("a")?.classList.contains("load-prev")){
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onLoadPrevClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
      };
    }.bind(this));

    // Reset state if collection is filtered
    document.addEventListener("collection:filtered", event => {
      this.latestPage = 1;
      this.earliestPage = 1;
      this.pagesLoaded = [1];
    });

    // Setup a waypoint for when reaching the end of the products grid, when
    // pagination is endless (on scroll)
    if(this.endlessMode === "scroll"){
      let nextPageWaypoint = new Waypoint({
        element: document.getElementById('products-container'),
        handler: function(direction) {
          if(direction === "down"){
            // console.log('[C] Scrolled down and reached waypoint');
            const loadMoreButton = this.container.querySelector(".load-more");
            // console.log("[C] Load more button", loadMoreButton);
            if(loadMoreButton){
              // console.log('[C] Clicking load more');
              loadMoreButton.click();
            }
          }
        }.bind(this),
        offset: 'bottom-in-view'
      });
    }
    
    if(this.endlessMode === "button" || this.endlessMode === "scroll") {

      let previousPageWaypoint = new Waypoint({
        element: document.getElementById('products-container'),
        handler: function(direction) {
          if(direction === "up"){
            // console.log('[C] Scrolled up and reached waypoint');
            const loadPrevButton = this.container.querySelector(".load-prev");
            // console.log("[C] Load prev button", loadPrevButton);
            if(loadPrevButton){
              // console.log('[C] Clicking load prev');
              loadPrevButton.click();
            }
          }
        }.bind(this),
        offset: 0
      });

      // Setup an IntersectionObserver to update the URL hash when the first or
      // last pagination tile is in view
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if(entry.isIntersecting){
            // console.log('[C] Intersect with firstlast', entry.target.dataset.page);

            let page = parseInt(entry.target.dataset.page);
            let queryParams = new URLSearchParams(window.location.search);
 
            // Set new or modify existing parameter value. 
            if(page > 1) {
              queryParams.set("page", entry.target.dataset.page);
            } else {
              queryParams.delete("page");
            }
 
            // Replace current querystring with the new one.
            history.replaceState(null, null, "?"+queryParams.toString());
          }
        })
      });
      
      this.grid.querySelectorAll("[data-page-first], [data-page-last]").forEach(tile => {
        this.observer.observe(tile);
      });
    }
  },

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage: function(searchParams, event, updateURLHash = true) {
    const params = new URLSearchParams(searchParams);
    let pageToRender = parseInt(params.get("page"));

    if(this.pagesLoaded.includes(pageToRender) || pageToRender < 1){
      // console.log(`[C] Page ${pageToRender} already loaded or < 1, skipping`);
      return;
    }

    document.getElementById('results-loading-collection').classList.remove('d-none');
    Waypoint.disableAll();
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    this.renderSectionFromFetch(url, event);

    if(pageToRender == 1){
      params.delete('page'); 
    }
    if (updateURLHash && pageToRender > this.latestPage) {
      this.updateURLHash(params.toString());
    }
  },

  /**
   * Fetch the section and re-render
   * 
   * @param {String} url URL to fetch and render 
   */
  renderSectionFromFetch: function(url) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        this.renderNewTiles(html);

        let newPage = parseInt(new DOMParser().parseFromString(html, 'text/html').getElementById('products-container').dataset.currentPage);
        // console.log("[C] Latest page loaded", this.latestPage);
        // console.log("[C] New page loaded", newPage);
        if(this.latestPage < newPage){
          // console.log("[C] Latest page < new page, so render pagination and update latest page")
          this.renderPagination(html);
          this.latestPage = newPage;
        } else {
          // console.log("[C] Latest page >= new page, so dont render pagination or update latest page, update earliestPAge")
          this.earliestPage = newPage;
        }

        this.pagesLoaded.push(newPage);

        document.getElementById('results-loading-collection').classList.add('d-none');

        mp_lazyload_setup();
        Waypoint.enableAll()
        Waypoint.refreshAll();

        var event = new CustomEvent("collection:updated");
        document.dispatchEvent(event);

        // Alert SWYM so it can instantiate wishlist buttons
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
      });
  },

  /**
   * Extract and append the new product tiles' HTML
   * 
   * @param {String} html The HTML returned from fetch 
   */
  renderNewTiles: function(html) {
    // Extract the new product tiles from the returned HTML
    let newTiles = Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll("[data-page]"));
    let newTilesPage = parseInt(newTiles[0].dataset.page);

    // Append the new page of tiles to a document fragment
    const fragment = document.createDocumentFragment();
    newTiles.forEach(function(element) {
      fragment.appendChild(element);
    }.bind(this));

    // console.log("[C] New tiles", newTiles);

    // Append fragment to the existing grid
    if(newTiles[0].dataset.page >= this.latestPage){
      // console.log("[C] Tiles from a later page, placing tiles after");
      this.grid.appendChild(fragment);

    } else {
      let injectPoint = this.grid.querySelector(`[data-page="${newTilesPage + 1}"]`);
      // console.log("[C] Tiles from an earlier page, placing tiles before");
      // console.log("[C] Inject point element", injectPoint);
      this.grid.insertBefore(fragment, injectPoint);
      injectPoint.scrollIntoView({
        behavior: 'instant',
      });
    }

    if(this.observer){
      this.grid.querySelectorAll("[data-page-first], [data-page-last]").forEach(function(tile) {
        this.observer.observe(tile);
      }.bind(this));
    }
  },

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html The HTML returned from fetch
   */
  renderPagination: function(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  },

  /**
   * Start the process of loading in new product tiles
   * 
   * @param {Event} event The initiating event
   */
  onLoadMoreClick: function(event) {
    event.preventDefault();
    const queryParamsString = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const queryParams = new URLSearchParams(queryParamsString);
    queryParams.set('page', this.latestPage + 1);

    // console.log('[C] Moreclick queryParams was', queryParamsString);
    // console.log('[C] Moreclick as querparma', queryParams.toString());

    this.renderPage(queryParams.toString());
  },

  /**
   * Start the process of loading in new product tiles
   * 
   * @param {Event} event The initiating event
   */
  onLoadPrevClick: function(event) {
    event.preventDefault();
    const queryParamsString = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    
    // Pagination is only updated if the latest page is extended, so we need to
    // manually set to our earliest page minus 1
    const queryParams = new URLSearchParams(queryParamsString);
    queryParams.set('page', this.earliestPage - 1);

    // console.log('[C] Prevclick queryParams was', queryParamsString);
    // console.log('[C] prevclick as querparma', queryParams.toString());

    this.renderPage(queryParams.toString());
  },

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash: function(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }
}

export default Collection;